import { useEffect, useState } from "react";
import { Quote, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePublicTeacherReviews, useMyReviewFor } from "@/hooks/use-reviews";
import { cn } from "@/lib/utils";
import { ReviewDialog } from "@/components/student/reviews/ReviewDialog";
import type { PublicReviewItem } from "@/services/reviewService";

interface TeacherReviewsSectionProps {
  /** UUID or handle — passed straight through to the public reviews endpoint. */
  teacherId: string;
  /** The teacher's user UUID — used for review eligibility + upsert (never the handle). */
  teacherUserId: string;
  teacherName: string;
}

interface AggregateInfo {
  ratingAvg: number | null;
  ratingCount: number;
  totalPages: number;
  totalElements: number;
}

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn("w-4 h-4", i < rating ? "text-warning fill-warning" : "text-border")}
        />
      ))}
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function TeacherReviewsSection({ teacherId, teacherUserId, teacherName }: TeacherReviewsSectionProps) {
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<PublicReviewItem[]>([]);
  const [aggregate, setAggregate] = useState<AggregateInfo | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  // Server-authoritative eligibility for the write affordance. The hook is internally
  // gated on the release flag + auth readiness; an anonymous/ineligible caller gets no data.
  const { data: eligibility } = useMyReviewFor(teacherUserId);

  const { data, isLoading, isFetching, isError } = usePublicTeacherReviews(
    teacherId,
    page,
    10
  );

  useEffect(() => {
    if (!data) return;
    setItems((prev) => (page === 0 ? data.content : [...prev, ...data.content]));
    setAggregate({
      ratingAvg: data.ratingAvg,
      ratingCount: data.ratingCount,
      totalPages: data.totalPages,
      totalElements: data.totalElements,
    });
  }, [data, page]);

  const handleLoadMore = () => setPage((p) => p + 1);

  // Server-authoritative write affordance (AC1/AC7). Only an eligible caller sees it;
  // an ineligible or anonymous visitor gets no data (and thus no button).
  const ownReview = eligibility?.review ?? null;
  const ownReviewPending = ownReview?.status === "PENDING" || ownReview?.status === "HELD";
  const ownReviewRemoved = ownReview?.status === "REMOVED";
  const canWrite = eligibility?.eligible === true;
  const writeAffordance = canWrite ? (
    ownReviewPending ? (
      <p className="mt-2 text-sm text-muted-foreground">Your review is under review.</p>
    ) : (
      <div className="mt-2 space-y-2">
        {ownReviewRemoved && (
          // The author's review was taken down by moderation — surface the sanitized
          // reason (ADR-125) and let them revise + resubmit.
          <p role="alert" className="text-sm text-destructive">
            Your review was removed by moderation
            {ownReview?.moderationReason ? `: ${ownReview.moderationReason}` : "."}
            {ownReview?.moderationReason ? "." : ""} You can edit it and resubmit.
          </p>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setReviewOpen(true)}
          className="cursor-pointer gap-1"
        >
          <Star className="w-4 h-4" />
          {ownReviewRemoved ? "Edit your review" : "Rate this teacher"}
        </Button>
        <ReviewDialog
          teacherUserId={teacherUserId}
          teacherName={teacherName}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          hideTrigger
        />
      </div>
    )
  ) : null;

  // Initial loading — skeleton.
  if (page === 0 && isLoading) {
    return (
      <Card className="border-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Quote className="w-5 h-5 text-primary" />
            Student Reviews
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-start gap-4">
              <Skeleton className="w-12 h-12 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  // Error on first load — inline, non-blocking (doesn't break the rest of the profile page).
  if (isError && !aggregate) {
    return (
      <Card className="border-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Quote className="w-5 h-5 text-primary" />
            Student Reviews
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Could not load reviews right now. Please try again later.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Empty — zero reviews. Never render "0.0", never render the aggregate header.
  if (aggregate && aggregate.ratingCount === 0) {
    return (
      <Card className="border-primary/10">
        <CardContent className="p-8 text-center">
          <Quote className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">No reviews yet for {teacherName}.</p>
          <div className="flex justify-center">{writeAffordance}</div>
        </CardContent>
      </Card>
    );
  }

  const hasMore = aggregate ? page < aggregate.totalPages - 1 : false;
  const roundedAvg = aggregate?.ratingAvg != null ? Math.round(aggregate.ratingAvg) : 0;

  return (
    <Card className="border-primary/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Quote className="w-5 h-5 text-primary" />
          Student Reviews
        </CardTitle>
        {aggregate?.ratingAvg != null && (
          <div className="flex items-center gap-2 mt-1">
            <StarRow rating={roundedAvg} />
            <span className="text-sm font-semibold text-foreground">
              {aggregate.ratingAvg.toFixed(1)} · {aggregate.ratingCount}{" "}
              review{aggregate.ratingCount === 1 ? "" : "s"}
            </span>
          </div>
        )}
        {writeAffordance}
      </CardHeader>
      <CardContent className="space-y-4">
        {items.map((review) => (
          <article
            key={review.id}
            className="p-4 rounded-xl bg-gradient-to-br from-muted/50 to-muted/20 border border-border/30"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center ring-2 ring-primary/10 flex-shrink-0">
                <span className="text-lg font-bold text-muted-foreground">
                  {review.authorDisplayName?.charAt(0)?.toUpperCase() ?? "S"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                  <p data-testid="review-author" className="font-semibold text-foreground">{review.authorDisplayName}</p>
                  <StarRow rating={review.rating} />
                </div>
                <blockquote className="text-muted-foreground leading-relaxed">
                  {review.comment}
                </blockquote>
                <p className="text-xs text-muted-foreground mt-2">
                  {formatDate(review.createdAt)}
                  {new Date(review.updatedAt).getTime() > new Date(review.createdAt).getTime() && (
                    <> · Updated {formatDate(review.updatedAt)}</>
                  )}
                </p>
                {review.teacherReply != null && (
                  <div
                    className="mt-3 ml-1 pl-4 border-l-2 border-primary/30 bg-primary/5 rounded-r-lg py-2 px-3"
                    aria-label="Teacher's reply"
                  >
                    <p className="text-xs font-semibold text-primary mb-1">Teacher's reply</p>
                    <p className="text-sm text-muted-foreground">{review.teacherReply}</p>
                  </div>
                )}
              </div>
            </div>
          </article>
        ))}

        {isError && aggregate && (
          <p className="text-sm text-muted-foreground">
            Could not load more reviews. Please try again later.
          </p>
        )}

        {hasMore && (
          <Button
            variant="outline"
            onClick={handleLoadMore}
            disabled={isFetching}
            className="w-full cursor-pointer transition-colors duration-200"
          >
            {isFetching ? "Loading…" : "Load more"}
          </Button>
        )}

        <div aria-live="polite" className="sr-only">
          {isFetching
            ? "Loading more reviews"
            : `Showing ${items.length} of ${aggregate?.totalElements ?? 0} reviews`}
        </div>
      </CardContent>
    </Card>
  );
}
